import {
  HIGH_LEVEL_VALUES_TO_LABELS,
  METRIC_VALUES_TO_LABELS,
} from 'constants/metricValuesToLabels';
import { ASCENDING, DESCENDING } from 'constants/sortDirections';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { TemplateType } from 'constants/templateTypes';
import { TABLE_COLS } from 'modules/productAnalytics/snippets/components';
import { FinishedBadge, RecurringBadge, RunningBadge } from 'ui/badge';
import { RELATIVE_DATE_PRESETS, getAllTimeZones } from 'ui/datePicker/helpers';
import { colors } from 'ui/theme';
import { StyledAnchor } from './Styled';
import { CampaignFilters, CampaignStates, CampaignTypes, ReportEventLabel } from './types';

export const MESSAGING_INSIGHTS = 'MessagingInsights';

export const RECENTLY_VIEWED_LOCAL_STORAGE_KEY = 'recentlyViewedSavedViews';

export const CITY_DATA = 'City Data';
export const DEVICE_DATA = 'Device Data';

export const MediumOptions: Record<string, string> = {
  Email: 'Email',
  SMS: 'SMS',
  Push: 'Push',
  WebPush: 'Web Push',
  InApp: 'In-App',
  Embedded: 'Embedded',
  WhatsApp: 'WhatsApp',
};

export const LowerCaseMediumOptions: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  push: 'Push',
  webpush: 'WebPush',
  inapp: 'InApp',
};

export const AggregateInAppToBreakdown: Record<string, string> = {
  'Total Opens': 'Total In-app Opens Breakdown',
  // 'Unique Opens': 'Unique In-app Opens Breakdown',
  'Total Clicks': 'Total In-app Clicks Breakdown',
  // 'Unique Clicks': 'Unique In-app Clicks Breakdown',
  'Click Rate': 'In-App Click Rate Breakdown',
  'Total Delivered': 'Total In-apps Delivered Breakdown',
};

export const MediumInAppToBreakdown: Record<string, string> = {
  // 'Unique In-apps Delivered': 'Unique In-apps Delivered Breakdown',
  'Total In-app Closes': 'Total In-app Closes Breakdown',
  'Total In-app Deletes': 'Total In-app Deletes Breakdown',
  'Total Inbox Impressions': 'Total Inbox Impressions Breakdown',
};

export const InAppBreakdownToAggregate: Record<string, string> = {
  'Total In-app Opens Breakdown': 'Total Opens',
  'Unique In-app Opens Breakdown': 'Unique Opens',
  'Total In-app Clicks Breakdown': 'Total Clicks',
  'Unique In-app Clicks Breakdown': 'Unique Clicks',
  'In-App Click Rate Breakdown': 'Click Rate',
  'Total In-apps Delivered Breakdown': 'Total Delivered',
};

export const InAppMetricsToBreakdown: Record<string, string> = {
  TotalOpens: 'TotalInAppOpensBreakdown',
  UniqueOpens: 'UniqueInAppOpensBreakdown',
  TotalClicks: 'TotalInAppClicksBreakdown',
  UniqueClicks: 'UniqueInAppClicksBreakdown',
  ClickRate: 'InAppClickRateBreakdown',
  TotalDelivered: 'TotalInAppsDeliveredBreakdown',
  UniqueInAppDelivered: 'UniqueInAppsDeliveredBreakdown',
  TotalInAppClose: 'TotalInAppClosesBreakdown',
  TotalInAppDelete: 'TotalInAppDeletesBreakdown',
  TotalInboxImpression: 'TotalInboxImpressionsBreakdown',
};

export const colorsByMedium: Record<string, string> = {
  [MediumOptions.Email]: colors.purple50,
  [MediumOptions.SMS]: colors.mustard50,
  [MediumOptions.Push]: colors.cyan50,
  [MediumOptions.InApp]: colors.green50,
  [MediumOptions.WebPush]: colors.salmon50,
  [MediumOptions.Embedded]: colors.red60,
  [MediumOptions.WhatsApp]: colors.forest90,
};

export const hoverColorsByMedium: Record<string, string> = {
  [MediumOptions.Email]: colors.purple100,
  [MediumOptions.SMS]: colors.mustard100,
  [MediumOptions.Push]: colors.cyan100,
  [MediumOptions.InApp]: colors.green100,
  [MediumOptions.WebPush]: colors.salmon100,
  [MediumOptions.Embedded]: colors.red100,
  [MediumOptions.WhatsApp]: colors.forest100,
};

export const StateBadges = {
  Running: <RunningBadge />,
  Aborted: <RecurringBadge>Aborted</RecurringBadge>,
  Finished: <FinishedBadge />,
};

export const CampaignFilterDefaults: CampaignFilters = {
  states: [CampaignStates.RUNNING, CampaignStates.FINISHED],
  types: [
    CampaignTypes.BLAST,
    CampaignTypes.TRIGGERED,
    CampaignTypes.WITH_AI,
    CampaignTypes.FROM_WORKFLOW,
    CampaignTypes.WITH_EXPERIMENT,
  ],
};

export enum CampaignTypeCategory {
  ALL_MEDIUMS = 'All Mediums',
  EMBEDDED = 'Embedded',
}

export const CampaignTypeCategoryLabels = Object.values(CampaignTypeCategory).reduce(
  (acc, value) => {
    acc[value] = value;
    return acc;
  },
  {} as Record<string, string>,
);

export const CampaignTypeOptions = [
  {
    categoryId: CampaignTypeCategory.ALL_MEDIUMS,
    label: 'Blast',
    id: CampaignTypes.BLAST,
    value: CampaignTypes.BLAST,
  },
  {
    categoryId: CampaignTypeCategory.ALL_MEDIUMS,
    label: 'Triggered',
    id: CampaignTypes.TRIGGERED,
    value: CampaignTypes.TRIGGERED,
  },
  {
    categoryId: CampaignTypeCategory.ALL_MEDIUMS,
    label: 'With AI',
    id: CampaignTypes.WITH_AI,
    value: CampaignTypes.WITH_AI,
  },
  {
    categoryId: CampaignTypeCategory.ALL_MEDIUMS,
    label: 'Journey Triggered',
    id: CampaignTypes.FROM_WORKFLOW,
    value: CampaignTypes.FROM_WORKFLOW,
  },
  {
    categoryId: CampaignTypeCategory.ALL_MEDIUMS,
    label: 'With Experiment',
    id: CampaignTypes.WITH_EXPERIMENT,
    value: CampaignTypes.WITH_EXPERIMENT,
  },
];

export const TemplateTypeOptions = [
  {
    id: TemplateType.BusinessInitiated,
    value: TemplateType.BusinessInitiated,
    label: 'Business Initiated',
  },
  {
    id: TemplateType.UserInitiated,
    value: TemplateType.UserInitiated,
    label: 'User Initiated',
  },
];

export const NiceTemplateTypes = {
  [TemplateType.BusinessInitiated]: 'Business Initiated',
  [TemplateType.UserInitiated]: 'User Initiated',
};

export const NiceNameTypes = {
  Blast: 'Blast',
  Triggered: 'Triggered',
  WithAI: 'With AI',
  FromWorkflow: 'Journey Triggered',
  WithExperiment: 'With Experiment',
};

export const CampaignMetricLabels = {
  audience: 'AUDIENCE',
  delivery: 'DELIVERY',
  engagement: 'ENGAGEMENT',
  retention: 'RETENTION',
  conversions: 'CONVERSIONS',
};

export const CampaignCategoryArr = [
  'audience',
  'delivery',
  'engagement',
  'retention',
  'conversions',
];

export const CampaignMediumDefaults = (isWhatsAppGAEnabled: boolean) => ({
  Email: ['TotalEmailSend', 'EmailDeliveryRate', 'EmailOpenRate', 'EmailClickRate'],
  Push: ['TotalPushSend', 'PushDeliveryRate', 'PushOpenRate'],
  SMS: ['TotalSMSSend', 'SMSDeliveryRate'],
  WebPush: ['TotalWebPushSend', 'TotalWebPushClick'],
  InApp: ['TotalInAppSend', 'TotalInAppClick'],
  Embedded: ['TotalEmbeddedRetrieved', 'EmbeddedImpressionsRate', 'EmbeddedClickRate'],
  WhatsApp: isWhatsAppGAEnabled
    ? ['TotalWhatsAppSend', 'WhatsAppDeliveryRate', 'TotalWhatsAppRead', 'WhatsAppClickRate']
    : ['TotalWhatsAppSend', 'WhatsAppDeliveryRate'],
});

export const CampaignToHighLevelMetric: Record<string, string> = {
  TotalEmailSend: 'TotalSent',
  TotalEmailSendSkip: 'TotalSendSkips',
  EmailDeliveryRate: 'DeliveryRate',
  TotalEmailDelivered: 'TotalDelivered',
  TotalEmailBounce: 'TotalBounces',
  TotalEmailOpen: 'TotalOpens',
  UniqueEmailOpenOrClick: 'UniqueOpens',
  UniqueEmailOpen: 'UniqueOpens',
  TotalEmailClick: 'TotalClicks',
  UniqueEmailClick: 'UniqueClicks',
  EmailClickPerOpenRate: 'ClicksPerOpensRate',
  EmailClickRate: 'ClickRate',
  TotalEmailComplaint: 'TotalComplaints',
  EmailBounceRate: 'BounceRate',
  EmailUnsubscribeRate: 'UnsubscribeRate',
  TotalUnsubscribe: 'TotalUnsubscribes',
  PurchasePerEmailSendMille: 'PurchasesPerMille',
  ConversionPerEmailDeliveredRate: 'ConversionPerSendRate',
  RevenuePerEmailSendMille: 'RevenuePerMille',
  Revenue: 'Revenue',
  TotalPurchase: 'TotalPurchases',
  TotalPushSend: 'TotalSent',
  TotalPushSendSkip: 'TotalSendSkips',
  PushDeliveryRate: 'DeliveryRate',
  TotalPushDelivered: 'TotalDelivered',
  TotalPushOpen: 'TotalOpens',
  UniquePushOpen: 'UniqueOpens',
  TotalPushUninstall: 'TotalUninstalls',
  TotalPushBounce: 'TotalBounces',
  PushBounceRate: 'BounceRate',
  PushUnsubscribeRate: 'UnsubscribeRate',
  ConversionPerPushDeliveredRate: 'ConversionPerSendRate',
  RevenuePerPushSendMille: 'RevenuePerMille',
  PurchasePerPushSendMille: 'PurchasesPerMille',
  TotalSMSSend: 'TotalSent',
  TotalSMSSendSkip: 'TotalSendSkips',
  SMSDeliveryRate: 'DeliveryRate',
  TotalSMSDelivered: 'TotalDelivered',
  TotalSMSBounce: 'TotalBounces',
  SMSBounceRate: 'BounceRate',
  TotalSMSInbound: 'TotalReplies',
  TotalSMSClick: 'TotalClicks',
  UniqueSMSClick: 'UniqueClicks',
  SMSClickRate: 'ClickRate',
  SMSUnsubscribeRate: 'UnsubscribeRate',
  PurchasePerSMSSendMille: 'PurchasesPerMille',
  RevenuePerSMSSendMille: 'RevenuePerMille',
  ConversionPerSMSDeliveredRate: 'ConversionPerSendRate',
  TotalWebPushSend: 'TotalSent',
  TotalWebPushSendSkip: 'TotalSendSkips',
  TotalWebPushClick: 'TotalClicks',
  UniqueWebPushClick: 'UniqueClicks',
  ConversionPerWebPushSendRate: 'ConversionPerSendRate',
  TotalInAppSendSkip: 'TotalSendSkips',
  TotalInAppSend: 'TotalSent',
  TotalInAppOpen: 'TotalOpens',
  TotalInAppClick: 'TotalClicks',
  UniqueInAppClick: 'UniqueClicks',
  InAppClickRate: 'ClickRate',
};

export const MediumAgnosticLabel = 'Other/All mediums';

export const HighLevelMediumAgnosticMetrics = new Set([
  'Revenue',
  'Total Purchases',
  'Total Conversions',
  'Total Unsubscribes',
  'Conversions / Send Delivered',
  'Conversions / Hold Out',
]);

export const HighLevelMetricDefaults = ['TotalSent', 'DeliveryRate', 'ClickRate', 'TotalOpens'];

export const NonMediumSpecificMetricOptions = [
  {
    categoryId: 'retention',
    label: 'Unique Unsubscribes',
    id: 'UniqueUnsubscribe',
    value: 'UniqueUnsubscribe',
    doubleLine: 'All',
  },
  {
    categoryId: 'conversions',
    label: 'Unique Custom Conversions',
    id: 'UniqueCustomConversion',
    value: 'UniqueCustomConversion',
    doubleLine: 'All',
  },
];
export const NonMediumSpecificMetricOptionLabels = new Set(
  NonMediumSpecificMetricOptions.map((metric) => metric.label),
);

// BE currently doesn't return responses for the following metrics (ref. ANA-1582)
export const MissingMetrics = new Set([
  'Email Unsubscribe Rate',
  'Push Unsubscribe Rate',
  'SMS Unsubscribe Rate',
  'Conversions / Unique Emails Delivered',
  'Conversions / Unique Pushes Delivered',
  'Conversions / Unique SMS Delivered',
  'Conversions / Unique SMS Sends',
  'Conversions / Email Hold Outs',
  'Conversions / Push Hold Outs',
  'Conversions / SMS Hold Outs',
  'Conversions / Web Push Hold Outs',
  'Conversions / Web Push Sends',
  'Purchases / M (Email)',
  'Purchases / M (Push)',
  'Purchases / M (SMS)',
  'Revenue / M (Email)',
  'Revenue / M (Push)',
  'Revenue / M (SMS)',
]);

export const ReportValues = (
  isWhatsAppGAEnabled: boolean,
): { [medium: string]: ReportEventLabel[] } => ({
  Email: [
    { badgeText: '', value: 'emailClick', id: 'email-emailClick', label: 'Email Click' },
    { badgeText: '', value: 'emailSendSkip', id: 'email-emailSendSkip', label: 'Email Send Skip' },
    { badgeText: '', value: 'emailSend', id: 'email-emailSend', label: 'Email Send' },
    { badgeText: '', value: 'purchase', id: 'email-purchase', label: 'Purchase' },
    {
      badgeText: '',
      value: 'emailUnSubscribe',
      id: 'email-emailUnSubscribe',
      label: 'Email Unsubscribe',
    },
    {
      badgeText: '',
      value: 'hostedUnsubscribeClick',
      id: 'email-hostedUnsubscribeClick',
      label: 'Hosted Unsubscribe Click',
    },
    {
      badgeText: '',
      value: 'emailComplaint',
      id: 'email-emailComplaint',
      label: 'Email Complaint',
    },
    { badgeText: '', value: 'customEvent', id: 'email-customEvent', label: 'Custom Event' },
    { badgeText: '', value: 'emailBounce', id: 'email-emailBounce', label: 'Email Bounce' },
    { badgeText: '', value: 'emailOpen', id: 'email-emailOpen', label: 'Email Open' },
    {
      badgeText: '',
      value: 'emailSubscribe',
      id: 'email-emailSubscribe',
      label: 'Email Subscribe',
    },
  ],
  InApp: [
    { badgeText: '', value: 'inAppDelete', id: 'inApp-inAppDelete', label: 'In App Delete' },
    { badgeText: '', value: 'inAppDelivery', id: 'inApp-inAppDelivery', label: 'In App Delivery' },
    {
      badgeText: '',
      value: 'inboxMessageImpression',
      id: 'inApp-inboxMessageImpression',
      label: 'Inbox Message Impression',
    },
    { badgeText: '', value: 'inAppClose', id: 'inApp-inAppClose', label: 'In App Close' },
    { badgeText: '', value: 'purchase', id: 'inApp-purchase', label: 'Purchase' },
    { badgeText: '', value: 'inAppSend', id: 'inApp-inAppSend', label: 'In App Send' },
    { badgeText: '', value: 'inAppOpen', id: 'inApp-inAppOpen', label: 'In App Open' },
    {
      badgeText: '',
      value: 'emailUnSubscribe',
      id: 'inApp-emailUnSubscribe',
      label: 'In App Unsubscribe',
    },
    { badgeText: '', value: 'customEvent', id: 'inApp-customEvent', label: 'Custom Event' },
    { badgeText: '', value: 'inAppClick', id: 'inApp-inAppClick', label: 'In App Click' },
    {
      badgeText: '',
      value: 'emailSubscribe',
      id: 'inApp-emailSubscribe',
      label: 'In App Subscribe',
    },
    { badgeText: '', value: 'inAppSendSkip', id: 'inApp-inAppSendSkip', label: 'In App Send Skip' },
    { badgeText: '', value: 'inboxSession', id: 'inApp-inboxSession', label: 'Inbox Session' },
  ],
  Push: [
    { badgeText: '', value: 'pushSend', id: 'push-pushSend', label: 'Push Send' },
    { badgeText: '', value: 'pushBounce', id: 'push-pushBounce', label: 'Push Bounce' },
    { badgeText: '', value: 'pushOpen', id: 'push-pushOpen', label: 'Push Open' },
    { badgeText: '', value: 'pushUninstall', id: 'push-pushUninstall', label: 'Push Uninstall' },
    { badgeText: '', value: 'pushSendSkip', id: 'push-pushSendSkip', label: 'Push Send Skip' },
    { badgeText: '', value: 'purchase', id: 'push-purchase', label: 'Purchase' },
    {
      badgeText: '',
      value: 'emailUnSubscribe',
      id: 'push-emailUnSubscribe',
      label: 'Push Unsubscribe',
    },
    { badgeText: '', value: 'customEvent', id: 'push-customEvent', label: 'Custom Event' },
    { badgeText: '', value: 'emailSubscribe', id: 'push-emailSubscribe', label: 'Push Subscribe' },
  ],
  SMS: [
    { badgeText: '', value: 'smsBounce', id: 'sms-smsBounce', label: 'SMS Bounce' },
    { badgeText: '', value: 'smsReceived', id: 'sms-smsReceived', label: 'SMS Received' },
    { badgeText: '', value: 'purchase', id: 'sms-purchase', label: 'Purchase' },
    { badgeText: '', value: 'smsClick', id: 'sms-smsClick', label: 'SMS Clicks' },
    {
      badgeText: '',
      value: 'emailUnSubscribe',
      id: 'sms-emailUnSubscribe',
      label: 'SMS Unsubscribe',
    },
    { badgeText: '', value: 'customEvent', id: 'sms-customEvent', label: 'Custom Event' },
    { badgeText: '', value: 'smsSendSkip', id: 'sms-smsSendSkip', label: 'SMS Send Skip' },
    { badgeText: '', value: 'smsSend', id: 'sms-smsSend', label: 'SMS Send' },
    { badgeText: '', value: 'emailSubscribe', id: 'sms-emailSubscribe', label: 'SMS Subscribe' },
  ],
  WebPush: [
    {
      badgeText: '',
      value: 'webPushSendSkip',
      id: 'webPush-webPushSendSkip',
      label: 'Web Push Send Skip',
    },
    { badgeText: '', value: 'purchase', id: 'webPush-purchase', label: 'Purchase' },
    {
      badgeText: '',
      value: 'emailUnSubscribe',
      id: 'webPush-emailUnSubscribe',
      label: 'Web Push Unsubscribe',
    },
    { badgeText: '', value: 'webPushClick', id: 'webPush-webPushClick', label: 'Web Push Click' },
    { badgeText: '', value: 'customEvent', id: 'webPush-customEvent', label: 'Custom Event' },
    { badgeText: '', value: 'webPushSend', id: 'webPush-webPushSend', label: 'Web Push Send' },
    {
      badgeText: '',
      value: 'emailSubscribe',
      id: 'webPush-emailSubscribe',
      label: 'Web Push Subscribe',
    },
  ],
  Embedded: [
    { badgeText: '', value: 'embeddedSend', id: 'embedded-embeddedSend', label: 'Embedded Send' },
    {
      badgeText: '',
      value: 'embeddedClick',
      id: 'embedded-embeddedClick',
      label: 'Embedded Click',
    },
    {
      badgeText: '',
      value: 'embeddedReceived',
      id: 'embedded-embeddedReceived',
      label: 'Embedded Received',
    },
    {
      badgeText: '',
      value: 'embeddedImpression',
      id: 'embedded-embeddedImpression',
      label: 'Embedded Impression',
    },
  ],
  WhatsApp: [
    { badgeText: '', value: 'whatsAppSend', id: 'whatsApp-whatsAppSend', label: 'WhatsApp Sends' },
    {
      badgeText: '',
      value: 'whatsAppSendSkip',
      id: 'whatsApp-whatsAppSendSkip',
      label: 'WhatsApp Send Skips',
    },
    {
      badgeText: '',
      value: 'whatsAppBounce',
      id: 'whatsApp-whatsAppBounce',
      label: 'WhatsApp Bounced',
    },
    {
      badgeText: '',
      value: 'whatsAppUnSubscribe',
      id: 'whatsApp-whatsAppUnSubscribe',
      label: 'WhatsApp Unsubscribe',
    },
    ...(isWhatsAppGAEnabled
      ? [
          {
            badgeText: '',
            value: 'whatsAppClick',
            id: 'whatsApp-whatsAppClick',
            label: 'WhatsApp Clicks',
          },
          {
            badgeText: '',
            value: 'whatsAppSend',
            id: 'whatsApp-whatsAppSend',
            label: 'WhatsApp Sends',
          },
        ]
      : []),
  ],
});

export const mapRegions = [
  {
    id: 'world',
    value: 'world',
    label: 'World',
  },
  {
    id: 'US',
    value: 'US',
    label: 'United States',
  },
  {
    id: '021',
    value: '021',
    label: 'North America',
  },
  {
    id: '005',
    value: '005',
    label: 'South America',
  },
  {
    id: '013',
    value: '013',
    label: 'Central America',
  },
  {
    id: '002',
    value: '002',
    label: 'Africa',
  },
  {
    id: '017',
    value: '017',
    label: 'Central Africa',
  },
  {
    id: '015',
    value: '015',
    label: 'Northern Africa',
  },
  {
    id: '018',
    value: '018',
    label: 'Southern Africa',
  },
  {
    id: '009',
    value: '009',
    label: 'Oceania',
  },
  {
    id: '030',
    value: '030',
    label: 'Eastern Asia',
  },
  {
    id: '034',
    value: '034',
    label: 'Southern Asia',
  },
  {
    id: '035',
    value: '035',
    label: 'Asia/Pacific Region',
  },
  {
    id: '143',
    value: '143',
    label: 'Central Asia',
  },
  {
    id: '145',
    value: '145',
    label: 'Middle East',
  },
  {
    id: '151',
    value: '151',
    label: 'Northern Asia',
  },
  {
    id: '154',
    value: '154',
    label: 'Northern Europe',
  },
  {
    id: '155',
    value: '155',
    label: 'Western Europe',
  },
  {
    id: '039',
    value: '039',
    label: 'Southern Europe',
  },
];

const DeviceChartColorPalette = [
  '#1abc9c',
  '#16a085',
  '#f1c40f',
  '#f39c12',
  '#27ae60',
  '#2ecc71',
  '#e67e22',
  '#d35400',
  '#3498db',
  '#2980b9',
  '#e74c3c',
  '#c0392b',
  '#9b59b6',
  '#8e44ad',
  '#34495e',
  '#2c3e50',
  '#95a5a6',
  '#7f8c8d',
  '#ecf0f1',
  '#bdc3c7',
];
export const currencySet = new Set([
  'revenue',
  'revenue / m (email)',
  'revenue / m (push)',
  'revenue / m (sms)',
  'average order value',
]);
export const percentSet = new Set(['clicks / opens']);
export const ChartOptions = {
  chart: {
    type: 'variablepie',
  },
  credits: {
    enabled: false,
  },
  title: {
    text: undefined,
  },
  exporting: { enabled: false },
  plotOptions: {
    variablepie: {
      colors: DeviceChartColorPalette,
    },
  },
  tooltip: {
    enabled: true,
    pointFormat: '<b>{point.y}</b>',
  },
  series: [
    {
      minPointSize: 10,
      innerSize: '40%',
      dataLabels: {
        enabled: true,
        format: `<span style="color:${colors.textSecondary};">{point.name}</span><br/><span>{point.percentage:.1f}%</span>`,
      },
      data: [],
    },
  ],
};

export const FETCH_ERRORS = {
  FETCH_CAMPAIGNS: 'fetchCampaigns',
  FETCH_ALL_METRICS_FOR_CAMPAIGNS: 'fetchAllMetricsForCampaigns',
  FETCH_X_MED_AGG_STATS: 'fetchXMedAggStats',
  FETCH_INSIGHTS_REPORT_AS_CSV: 'fetchInsightReportasCSV',
  FETCH_SEGMENTS: 'fetchSegments',
  FETCH_SEGMENTSV2: 'fetchSegmentsV2',
  EXPORT_CAMPAIGNS_AS_CSV: 'exportCampaignsAsCsv',
  FETCH_SORTED_CAMPAIGN_IDS: 'fetchSortedCampaignIDsByMetric,',
  FETCH_SAVED_VIEW: 'fetchSavedView',
  FETCH_TIME_SERIES_DATA: 'fetchTimeSeriesData',
  FETCH_LOCATION_DATA: 'fetchLocationData',
  FETCH_DEVICE_DATA: 'fetchDeviceData',
  FETCH_PDF_REPORT: 'fetchPDFReport',
};

export const PROXIED_OPENS_DATE = new Date('2021-09-01T00:00:00');

export const PROXIED_OPENS_BANNER_CONTENT = (
  <>
    For the date range you’ve selected, open metrics may be impacted by recent privacy protection
    changes. To learn more, see{' '}
    <StyledAnchor
      href="https://iterable.com/blog/what-apples-ios-15-privacy-updates-mean-for-marketers/"
      target="_blank"
    >
      our blog
    </StyledAnchor>
    .
  </>
);

export const OpenMetrics = [
  'TotalOpens',
  'UniqueOpens',
  'TotalEmailOpen',
  'UniqueEmailOpen',
  'TotalEmailOpensFiltered',
  'UniqueEmailOpensFiltered',
  'TotalEmailOpensBotFiltered',
  'UniqueEmailOpensBotFiltered',
];

export const ToggletipForFilteredProxiedOpensMetrics: Record<string, string> = {
  'Total Email Opens':
    'This metric may be less reliable than Total Email Opens (filtered) because it includes pre-fetched and proxied opens, which can artificially inflate open rates.',
  'Total Email Opens (filtered)':
    'This metric may be more reliable than Total Email Opens because it excludes pre-fetched and proxied opens, which can artificially inflate open rates.',
  'Unique Email Opens':
    'This metric may be less reliable than Unique Email Opens (filtered) because it includes pre-fetched and proxied opens, which can artificially inflate open rates.',
  'Unique Email Opens (filtered)':
    'This metric may be more reliable than Unique Email Opens because it excludes pre-fetched and proxied opens, which can artificially inflate open rates.',
};

export const MaxNumCampaigns = 50000;
export const CampaignLimitText =
  'This page has reached its campaign limit. Showing the 50,000 most recently modified campaigns.';

export const ListIdSegs = new Set(['emailListIds', 'userListIds']);

export const BreakdownToInApp: Record<string, string> = {
  'Total In-app Opens Breakdown': 'Total In-app Opens',
  'Unique In-app Opens Breakdown': 'Unique In-app Opens',
  'Total In-app Clicks Breakdown': 'Total In-app Clicks',
  'Unique In-app Clicks Breakdown': 'Unique In-app Clicks',
  'In-app Click Rate Breakdown': 'In-app Click Rate',
  'Total In-apps Delivered Breakdown': 'Total In-apps Delivered',
  'Unique In-apps Delivered Breakdown': 'Unique In-apps Delivered',
  'Total In-app Closes Breakdown': 'Total In-app Closes',
  'Total In-app Deletes Breakdown': 'Total In-app Deletes',
  'Total Inbox Impressions Breakdown': 'Total Inbox Impressions',
};

export const FilterParamOptions = [
  'messageMediums',
  'workflowIds',
  'campaignIds',
  'labelIds',
  'messageTypeIds',
  'states',
  'types',
  'startTime',
  'endTime',
  'timePreset',
  'timezone',
  'metrics',
  'tab',
  'segment',
  'region',
  'campaignsSortBy',
  'campaignsSortDir',
  'audienceSortBy',
  'audienceSortDir',
  'snippetsSortBy',
  'snippetsSortDir',
];

export const QueryParamOptions = [...FilterParamOptions, 'previews', 'projectId'];

export const Tabs = {
  overview: 'overview',
  campaigns: 'campaigns',
  segments: 'segments',
  timeSeries: 'timeSeries',
  location: 'location',
  devices: 'devices',
  snippets: 'snippets',
};

export const TabLabels: { [tabValue: string]: string } = {
  overview: 'Overview',
  campaigns: 'Campaigns',
  segments: 'Audience',
  timeSeries: 'Time Series',
  location: 'Location',
  devices: 'Devices',
  snippets: 'Snippets',
};

export const timezoneOptions = getAllTimeZones();
export const QueryValueOptionsByParam: Record<string, string[]> = {
  params: QueryParamOptions,
  messageMediums: Object.keys(MediumOptions),
  states: Object.keys(StateBadges),
  types: CampaignTypeOptions.map((o) => o.id),
  metrics: [...Object.keys(METRIC_VALUES_TO_LABELS), ...Object.keys(HIGH_LEVEL_VALUES_TO_LABELS)],
  timePreset: Object.keys(RELATIVE_DATE_PRESETS),
  timezone: timezoneOptions,
  tab: Object.values(Tabs),
  region: mapRegions.map((reg) => reg.value),
  campaignsSortBy: [
    ...Object.values(METRIC_VALUES_TO_LABELS),
    ...Object.values(HIGH_LEVEL_VALUES_TO_LABELS),
    'medium',
    'campaignName',
    'launchDate',
    'id',
  ],
  campaignsSortDir: [ASCENDING, DESCENDING],
  audienceSortBy: [
    ...Object.values(METRIC_VALUES_TO_LABELS),
    ...Object.values(HIGH_LEVEL_VALUES_TO_LABELS),
    'Segment',
    'EmailListName',
  ],
  audienceSortDir: [ASCENDING, DESCENDING],
  snippetsSortBy: TABLE_COLS?.map((col) => col.value),
  snippetsSortDir: [SORT_DIRECTIONS.ASCENDING, SORT_DIRECTIONS.DESCENDING],
};

/** PDF Report Modal */
export const PDF_REPORT_CONTENT = {
  title: 'Select which pages you want included in the report:',
  fieldLabelName: 'Name',
  fieldLabelOverview: 'Overview',
  fieldDescriptionOverview: 'Displays up to 10 metrics based on your selected campaign filters',
  fieldLabelCampaigns: 'Campaigns',
  fieldDescriptionCampaigns:
    'Breakdown of all campaigns based on your selected campaign filters in the given timeframe',
  fieldLabelAudience: 'Audience',
  fieldDescriptionAudience: 'Breakdown of the selected segment across your selected mediums',
  fieldPlaceholderAudience: 'Select a segment',
  fieldLabelTimeSeries: 'Time series',
  fieldDescriptionTimeSeries: 'Charts displaying selected metrics over the given timeframe',
  fieldLabelLocations: 'Locations',
  fieldDescriptionLocations:
    'Heatmap and breakdown of cities within the selected region where your campaigns were interacted with',
  fieldPlaceholderLocations: 'Select a region',
  fieldLabelDevices: 'Devices',
  fieldDescriptionDevices: 'Breakdown of devices used to interact with your campaigns',
};

/** Feature Flags */
export const DYNAMIC_HEATMAP = 'AnalyticsDynamicHeatmaps';
export const APPLY_BUTTON = 'MIApplyButton';
export const MIExportPDF = 'MIExportPDF';
export const DYNAMIC_CONTENT_REPORTING_SNIPPET = 'DynamicContentReportingSnippets';
export const IN_APP_REPORTING = 'WebInAppReporting';
